// ═══════════════════════════════════════════════════════════════
// BenDashboard — Dashboard Personnel Admin (données réelles)
// Session 32 — Remplacement des données hardcodées
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import { Server, Users, BarChart3, DollarSign, Zap, Activity, FolderOpen, MessageSquare, RefreshCw } from 'lucide-react';
import ActiveUsersPanel from '../Admin/ActiveUsersPanel';

const formatCost = (usd) => {
  if (!usd || usd === 0) return '$0.00';
  if (usd < 1) return `$${usd.toFixed(3)}`;
  return `$${usd.toFixed(2)}`;
};

const BenDashboard = ({ onProjectSelect }) => {
  const [infra, setInfra] = useState(null);
  const [costs, setCosts] = useState(null);
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadAll = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      const token = localStorage.getItem('oracle_token');
      const headers = { Authorization: `Bearer ${token}` };
      const [infraRes, costsRes, usersRes, logsRes] = await Promise.all([
        fetch('/api/admin/infrastructure', { headers }).then(r => r.ok ? r.json() : null).catch(() => null),
        fetch('/api/costs/dashboard?days=30', { headers }).then(r => r.ok ? r.json() : null).catch(() => null),
        fetch('/api/admin/users', { headers }).then(r => r.ok ? r.json() : null).catch(() => null),
        fetch('/api/admin/logs?days=1&type=all', { headers }).then(r => r.ok ? r.json() : null).catch(() => null),
      ]);
      if (infraRes) setInfra(infraRes);
      if (costsRes) setCosts(costsRes);
      if (usersRes) setUsers(usersRes.users || usersRes || []);
      if (logsRes) setLogs(logsRes);
    } catch (e) {
      console.error('Erreur dashboard:', e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>Chargement dashboard admin...</div>;

  const cardStyle = {
    background: 'var(--card-bg)', border: '1px solid var(--card-border)',
    borderRadius: '12px', padding: '20px',
  };

  const statBox = (icon, label, value, color) => (
    <div style={{ ...cardStyle, display: 'flex', alignItems: 'center', gap: '14px' }}>
      <div style={{
        width: '42px', height: '42px', borderRadius: '10px',
        background: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {React.cloneElement(icon, { size: 20, style: { color } })}
      </div>
      <div>
        <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '2px' }}>{label}</div>
        <div style={{ fontSize: '20px', fontWeight: '700', color: 'var(--text)' }}>{value}</div>
      </div>
    </div>
  );

  // Infrastructure status
  const services = infra?.services || {};
  const allOk = Object.values(services).every(s => s.status === 'ok');
  const serverMetrics = services.server?.metrics || {};
  const pgMetrics = services.postgresql?.metrics || {};

  // Costs
  const totalCost = costs?.totals?.cost_usd || 0;
  const totalEvents = costs?.totals?.events || 0;

  // Logs stats
  const todayEvents = logs?.stats?.total_events || 0;
  const todayErrors = logs?.stats?.errors || 0;
  const todayLogins = logs?.stats?.logins || 0;

  // Active users
  const activeUsers = users.filter(u => u.active !== false);

  return (
    <div style={{ padding: '24px', height: '100%', overflow: 'auto' }}>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
        <div>
          <h2 style={{ fontSize: '22px', fontWeight: '700', color: 'var(--text)', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <BarChart3 size={22} style={{ color: '#3b82f6' }} />
            Dashboard Personnel
          </h2>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '4px' }}>
            Vue d'ensemble administration
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            padding: '6px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: '600',
            display: 'flex', alignItems: 'center', gap: '6px',
            background: allOk ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
            color: allOk ? '#10b981' : '#ef4444',
            border: `1px solid ${allOk ? 'rgba(16,185,129,0.2)' : 'rgba(239,68,68,0.2)'}`,
          }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: allOk ? '#10b981' : '#ef4444' }} />
            {allOk ? 'Systeme operationnel' : 'Service(s) en erreur'}
          </div>
          <button onClick={() => loadAll(true)} style={{
            padding: '8px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: '500',
            cursor: 'pointer', border: 'none', display: 'flex', alignItems: 'center', gap: '6px',
            background: 'linear-gradient(135deg, #2563eb, #1d4ed8)', color: 'white',
          }}>
            <RefreshCw size={13} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
          </button>
        </div>
      </div>

      {/* Stats cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        {statBox(<DollarSign />, 'Cout total (30j)', formatCost(totalCost), '#10b981')}
        {statBox(<Zap />, 'Appels LLM (30j)', totalEvents.toLocaleString(), '#3b82f6')}
        {statBox(<Users />, 'Utilisateurs actifs', String(activeUsers.length), '#8b5cf6')}
        {statBox(<FolderOpen />, 'Projets', String(pgMetrics.projects || 0), '#f59e0b')}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
        {/* Infrastructure */}
        <div style={cardStyle}>
          <h3 style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text)', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Server size={16} style={{ color: '#3b82f6' }} />
            Infrastructure
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {Object.entries(services).map(([key, svc]) => {
              const names = { postgresql: 'PostgreSQL', redis: 'Redis', weaviate: 'Weaviate', nextcloud: 'ITRIIA Cloud', n8n: 'N8N', server: 'Node.js' };
              return (
                <div key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '13px', color: 'var(--text)' }}>{names[key] || key}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{svc.version?.split(' ').pop() || ''}</span>
                    <span style={{
                      padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: '600',
                      background: svc.status === 'ok' ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
                      color: svc.status === 'ok' ? '#10b981' : '#ef4444',
                    }}>
                      {svc.status === 'ok' ? 'En ligne' : 'Erreur'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Activite 24h */}
        <div style={cardStyle}>
          <h3 style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text)', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Activity size={16} style={{ color: '#f59e0b' }} />
            Activite (24h)
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Evenements</span>
                <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text)' }}>{todayEvents}</span>
              </div>
            </div>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Connexions</span>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#3b82f6' }}>{todayLogins}</span>
              </div>
            </div>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Erreurs</span>
                <span style={{ fontSize: '14px', fontWeight: '600', color: todayErrors > 0 ? '#ef4444' : '#10b981' }}>{todayErrors}</span>
              </div>
            </div>
            <div style={{ borderTop: '1px solid var(--card-border)', paddingTop: '10px', marginTop: '4px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Serveur uptime</span>
                <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text)' }}>{serverMetrics.uptime || 'N/A'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px' }}>
                <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Heap memoire</span>
                <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text)' }}>{serverMetrics.heap_used || 'N/A'} / {serverMetrics.heap_total || 'N/A'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px' }}>
                <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Load average</span>
                <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text)' }}>{serverMetrics.load_avg || 'N/A'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Couts par projet */}
      {costs?.by_project && costs.by_project.length > 0 && (
        <div style={{ ...cardStyle, marginBottom: '24px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text)', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <DollarSign size={16} style={{ color: '#10b981' }} />
            Couts par projet (30j)
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {costs.by_project.slice(0, 5).map((p, i) => {
              const maxCost = Math.max(...costs.by_project.map(x => x.cost_usd), 0.001);
              return (
                <div key={i} onClick={() => onProjectSelect && p.project_id && onProjectSelect({ id: p.project_id, name: p.name })}
                  style={{ cursor: onProjectSelect && p.project_id ? 'pointer' : 'default', padding: '4px 0', borderRadius: '6px', transition: 'background 0.15s' }}
                  onMouseEnter={e => onProjectSelect && p.project_id && (e.currentTarget.style.background = 'rgba(59,130,246,0.08)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' }}>
                    <span style={{ color: 'var(--text)', fontWeight: '500' }}>{p.name}</span>
                    <span style={{ color: '#10b981', fontWeight: '600' }}>{formatCost(p.cost_usd)}</span>
                  </div>
                  <div style={{ height: '6px', background: 'var(--input-bg)', borderRadius: '3px', overflow: 'hidden' }}>
                    <div style={{
                      width: `${(p.cost_usd / maxCost) * 100}%`,
                      height: '100%', borderRadius: '3px',
                      background: 'linear-gradient(90deg, #10b981, #059669)',
                      transition: 'width 0.5s ease',
                    }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Equipe */}
      <div style={cardStyle}>
        <h3 style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text)', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Users size={16} style={{ color: '#8b5cf6' }} />
          Equipe
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
          {activeUsers.map(u => {
            const roleLabels = {
              admin_general: { label: 'Admin', color: '#ef4444' },
              oracle_fondateur: { label: 'Fondateur', color: '#f59e0b' },
              collaboratrice: { label: 'Collaboratrice', color: '#8b5cf6' },
              collaborateur: { label: 'Collaborateur', color: '#3b82f6' },
              lecteur: { label: 'Lecteur', color: '#6b7280' },
            };
            const role = roleLabels[u.role] || { label: u.role, color: '#6b7280' };
            const lastLogin = u.last_login ? new Date(u.last_login).toLocaleDateString('fr-FR') : 'Jamais';
            return (
              <div key={u.id} style={{
                padding: '14px', borderRadius: '10px', background: 'var(--input-bg)',
                border: '1px solid var(--card-border)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text)' }}>
                    {u.full_name || u.username}
                  </span>
                  <span style={{
                    padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: '600',
                    background: `${role.color}15`, color: role.color,
                  }}>
                    {role.label}
                  </span>
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                  Derniere connexion : {lastLogin}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Session 36 — Utilisateurs en ligne (live) */}
      <div style={{ marginTop: '24px' }}>
        <ActiveUsersPanel />
      </div>
    </div>
  );
};

export default BenDashboard;
